import React, { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Container } from '@mantine/core';
import {
  IconBellRinging,
  IconBodyScan,
  IconChartArcs,
  IconDashboard,
  IconFriends,
  IconHeartbeat,
  IconReportMedical,
  IconBook,
  IconNotebook,
} from '@tabler/icons-react';
import Shell from '../../global/component/organism/Shell/Shell';
import { useSessionProvider } from '../../global/provider/sessionContext';
import { useParams } from 'react-router';
import { usePostAcceptInvitation } from '../../global/entities/PatientInvitations/mutations/usePostAcceptInvitationMutations.tsx';
import classes from '../../global/style/Classes.module.less';
import { RouteStore } from '../../global/Store/RouteStore.ts';
import { inject, observer } from 'mobx-react';
import { PatientStore } from '../../global/Store/PatientStore.ts';
import { useTranslation } from 'react-i18next';
import EmailWarningSticky from '../../global/component/molecules/EmailWarningSticky/EmailWarningSticky';
import { useGetPatientsProfile } from '../../global/entities/Patients/queries/useGetPatientsProfiles.tsx';
import { usePatientAlerts } from '../../global/entities/Alert/queries';
import { useFeatureVisibility } from '../../global/provider/featureVisibilityContext';
import { useEICFConsentLock } from '../../global/component/organism/EICFConsentGuard/eicfConsentLockContext.ts';

export type IPatientDashboardShellProps = {
  routeStore?: RouteStore;
  patientStore?: PatientStore;
};

const PatientDashboardShell: React.FC<IPatientDashboardShellProps> = inject(
  'patientStore',
  'routeStore',
)(
  observer(({ patientStore }) => {
    const { currentUser, setCurrentUser } = useSessionProvider();
    const location = useLocation();
    const { guid } = useParams<{ guid: string }>();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const acceptInvitation = usePostAcceptInvitation();

    // The invitation acceptance gate has been removed: every condition is now
    // accepted on the previous (consent) screen. When a patient lands here with
    // an invitation guid we silently accept it server-side — which links the
    // profile to the HCP(s), creates the eICF consent and flags hasProfile —
    // then send them straight to the dashboard.
    const invitationAcceptedRef = useRef(false);
    useEffect(() => {
      if (!guid || invitationAcceptedRef.current) return;
      invitationAcceptedRef.current = true;
      const acceptAndContinue = async () => {
        try {
          await acceptInvitation.mutateAsync(guid);
        } catch (e) {
          console.error('Failed to accept invitation:', e);
        } finally {
          if (currentUser) setCurrentUser({ ...currentUser, hasProfile: true });
          navigate('/patientDashboard/mainPatient');
        }
      };
      acceptAndContinue();
    }, [guid]);

    // Fetch patient profile data
    const { data: patientProfile, isLoading: isLoadingProfile } =
      useGetPatientsProfile();
    const { data: patientAlerts } = usePatientAlerts();
    const { isFeatureVisible, isLoading: isLoadingFeatures } =
      useFeatureVisibility();
    const { isLocked: isEicfLocked } = useEICFConsentLock();
    const segments = location.pathname.split('/').filter(Boolean);

    const unreadAlertCount = useMemo(() => {
      if (!patientAlerts) return 0;
      return patientAlerts.filter(
        (a) =>
          !a.isVisited &&
          ['Open', 'Acknowledged', 'Escalated'].includes(a.status),
      ).length;
    }, [patientAlerts]);

    useEffect(() => {
      const setPatientData = async () => {
        if (currentUser?.patientID && patientStore) {
          try {
            // Only set if the patient ID has actually changed or if patient store is empty
            if (patientStore.patientId !== currentUser.patientID)
              await patientStore.setPatientId(currentUser.patientID);

            // Now populate the rest of the patient data from the profile
            if (patientProfile && !isLoadingProfile) {
              // Set patient name (firstName + lastName)
              const fullName =
                `${patientProfile.firstName} ${patientProfile.lastName}`.trim();
              if (fullName && patientStore.name !== fullName)
                await patientStore.setPatientName(fullName);

              // Set birth date
              if (
                patientProfile.birthDate &&
                patientStore.birthDate !== patientProfile.birthDate.toString()
              )
                {await patientStore.setPatientBirthDate(
                  patientProfile.birthDate.toString(),
                );}

              // Set therapeutic indication ID
              if (
                patientProfile.therapeuticIndicationID &&
                patientStore.therapeuticIndicationId !==
                  patientProfile.therapeuticIndicationID.toString()
              )
                {await patientStore.setTherapeuticIndicationId(
                  patientProfile.therapeuticIndicationID.toString(),
                );}

              // Set sex ID
              if (
                patientProfile.sexID &&
                patientStore.sexId !== patientProfile.sexID.toString()
              )
                await patientStore.setSexId(patientProfile.sexID.toString());

              // Set is anonymized mode
              if (
                patientStore.isAnonymizedMode !==
                patientProfile.isAnonymizedMode
              )
                {await patientStore.setIsAnonymizedMode(
                  patientProfile.isAnonymizedMode ?? false,
                );}
            }
          } catch (error) {
            console.error('Failed to initialize patient store:', error);
          }
        }
      };

      setPatientData();
    }, [currentUser, patientStore, patientProfile, isLoadingProfile]);

    const patientTabs = useMemo(
      () => [
        {
          ids: ['mainPatient', 'partialCollaborator', 'questionnaire', 'task'],
          icon: IconDashboard,
          title: t('navbar.mainPatient'),
        },
        {
          ids: ['patientMedicalInformation', 'editPrescription'],
          icon: IconReportMedical,
          title: t('navbar.patientMedicalInformation'),
          withTitle: true,
          featureCode: 'medicalInformation',
        },
        {
          ids: ['patientMedicalOverview'],
          icon: IconChartArcs,
          title: t('navbar.patientMedicalOverview'),
          withTitle: true,
          featureCode: 'medicalOverview',
        },
        {
          ids: ['biomarkers'],
          icon: IconBodyScan,
          title: t('navbar.biomarkers'),
          featureCode: 'labResults',
        },
        {
          ids: ['journals'],
          icon: IconNotebook,
          title: t('navbar.journals'),
          withTitle: true,
          featureCode: 'journal',
        },
        {
          ids: ['physiologicalData'],
          icon: IconHeartbeat,
          title: t('navbar.physiologicalData'),
          withTitle: true,
          featureCode: 'physiologicals',
        },
        {
          ids: ['collaborator'],
          icon: IconFriends,
          title: t('navbar.collaborator'),
          withTitle: true,
        },
        {
          ids: ['resources'],
          icon: IconBook,
          title: t('navbar.resources'),
          withTitle: true,
          featureCode: 'resources',
        },
        {
          ids: ['patientAlerts'],
          icon: IconBellRinging,
          title: t('alert.title'),
          badge: unreadAlertCount,
          featureCode: 'alerts',
        },
      ],
      [t, i18n.language, unreadAlertCount],
    );

    // Check if we're on the subscription plan page and disable sidebar (but not management page)
    const isSubscriptionPlanPage =
      location.pathname === '/patientDashboard/subscription' ||
      location.pathname === '/patientDashboard/subscription/success';
    const filteredTabs = useMemo(
      () =>
        patientTabs.filter(
          (tab) => !tab.featureCode || isFeatureVisible(tab.featureCode),
        ),
      [patientTabs, isFeatureVisible],
    );

    const firstAvailableRoute = filteredTabs[0]?.ids[0] ?? 'mainPatient';

    // Check if the current route belongs to a disabled feature
    const isCurrentRouteAccessible = useMemo(() => {
      const currentSegment = segments[1];
      if (!currentSegment) return true;

      const owningTab = patientTabs.find((tab) =>
        tab.ids.includes(currentSegment),
      );
      if (!owningTab?.featureCode) return true;
      return isFeatureVisible(owningTab.featureCode);
    }, [segments, patientTabs, isFeatureVisible]);

    // Redirect to the first available tab when on a disabled feature route
    useEffect(() => {
      if (
        !isLoadingFeatures &&
        !isCurrentRouteAccessible &&
        firstAvailableRoute
      )
        navigate(firstAvailableRoute, { replace: true });
    }, [
      isCurrentRouteAccessible,
      isLoadingFeatures,
      firstAvailableRoute,
      navigate,
    ]);

    const tabsToShow = useMemo(() => {
      const base = currentUser?.hasProfile ? filteredTabs : [];
      if (!isEicfLocked) return base;
      return base.map((tab) => ({
        ...tab,
        disabled: !tab.ids.includes('mainPatient'),
      }));
    }, [currentUser?.hasProfile, filteredTabs, isEicfLocked]);

    return (
      <>
        <Shell
          tabs={tabsToShow}
          route="patientDashboard"
          disabledTabs={isSubscriptionPlanPage}
        >
          <Container
            id="patient-dashboard-container"
            size="xl"
            className={classes.shellContainer}
          >
            {isCurrentRouteAccessible ? <Outlet /> : null}
          </Container>
        </Shell>
        <EmailWarningSticky />
      </>
    );
  }),
);

export default PatientDashboardShell;
